import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiX, FiArrowRight, FiSmartphone } from "react-icons/fi";

const services = [
  {
    id: 1,
    title: "WEBSITES",
    tag: "Crafting engaging online experiences",
    number: "01",
    description:
      "From stunning visuals to seamless functionality, we build websites that convert. Starting from AED 79.",
    items: [
      "Visual Content Strategy",
      "Competitive Analysis",
      "Key Messaging",
      "Research & Testing",
      "UI & UX Strategy",
      "Content Strategy",
    ],
    image:
      "https://i.pinimg.com/564x/70/20/f4/7020f453974ebf3d25268e55e983b816.jpg",
    gradient: "from-blue-500 to-cyan-400",
    color: "blue",
    icon: "🌐"
  },
  {
    id: 2,
    title: "MOBILE APPLICATIONS",
    tag: "Native and cross-platform mobile solutions",
    number: "02",
    description:
      "Build powerful iOS and Android apps with seamless user experiences. React Native & Flutter development. Starting from AED 1500.",
    items: [
      "iOS & Android Development",
      "React Native & Flutter",
      "App Store Deployment",
      "UI/UX Design",
      "API Integration",
      "Performance Optimization",
    ],
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1000&q=80",
    gradient: "from-purple-500 to-pink-500",
    color: "purple",
    icon: "📱"
  },
  {
    id: 3,
    title: "SOCIAL MEDIA",
    tag: "Elevate your brand's online presence",
    number: "03",
    description:
      "Engage your audience, build community, and drive traffic. Includes a free website. Starting from AED 1000.",
    items: [
      "Social Media Strategy",
      "Content Creation",
      "Community Management",
      "Analytics & Reporting",
      "Paid Advertising",
      "Brand Storytelling",
    ],
    image:
      "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?auto=format&fit=crop&w=1000&q=80",
    gradient: "from-green-500 to-emerald-400",
    color: "green",
    icon: "📢"
  },
  {
    id: 4,
    title: "CRM",
    tag: "Streamline your customer relationships",
    number: "04",
    description:
      "Automate sales, marketing, and customer service workflows for enhanced efficiency. Starting from AED 250/month.",
    items: [
      "Sales Automation",
      "Customer Database",
      "Marketing Campaigns",
      "Analytics Dashboard",
      "Integration Setup",
      "Custom Workflows",
    ],
    image:
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1000&q=80",
    gradient: "from-orange-500 to-amber-400",
    color: "orange",
    icon: "💼"
  },
  {
    id: 5,
    title: "ADVERTISEMENT",
    tag: "Maximize your reach and ROI",
    number: "05",
    description:
      "Targeted campaigns on Google and Meta to connect with your ideal customers. Google Ads (AED 599), Meta Ads (AED 599).",
    items: [
      "Google Ads Management",
      "Meta Ads Strategy",
      "Audience Targeting",
      "Conversion Tracking",
      "A/B Testing",
      "ROI Optimization",
    ],
    image:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1000&q=80",
    gradient: "from-red-500 to-rose-400",
    color: "red",
    icon: "🎯"
  },
];

const ServicesSection = () => {
  const [openId, setOpenId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  const toggleService = (id) => {
    setOpenId(openId === id ? null : id);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const hoveredService = services.find((s) => s.id === hoveredId);

  return (
    <div 
      className="relative min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-900 px-6 md:px-24 py-20 font-sans overflow-hidden"
      ref={sectionRef}
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-100 rounded-full blur-3xl opacity-30 translate-x-1/3 translate-y-1/3"></div>
      <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-cyan-100 rounded-full blur-3xl opacity-20"></div>
      
      {/* Enhanced cursor image with smooth animation */}
      {hoveredService && (
        <motion.div
          className="fixed pointer-events-none z-50 hidden lg:block"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            x: mousePosition.x + 20,
            y: mousePosition.y + 20
          }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <div className="relative">
            <img
              src={hoveredService.image}
              alt={hoveredService.title}
              className="w-80 h-48 object-cover rounded-2xl shadow-2xl border-4 border-white"
            />
            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${hoveredService.gradient} opacity-20`}></div>
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1 shadow-lg">
              <span className="text-sm font-semibold text-gray-800">{hoveredService.icon} {hoveredService.title}</span>
            </div>
          </div>
        </motion.div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div 
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">
            Comprehensive Digital Solutions
          </p>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent leading-tight">
            End-to-End Digital Services
          </h2>
          <p className="text-gray-600 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
            From mobile apps to websites and digital marketing - we provide complete solutions to transform your business.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-4">
          {services.map((service, index) => {
            const isOpen = openId === service.id;
            const isHovered = hoveredId === service.id;

            return (
              <motion.div
                key={service.id}
                className={`relative rounded-2xl transition-all duration-500 overflow-hidden border ${
                  isOpen 
                    ? `border-${service.color}-200 bg-${service.color}-50 shadow-xl` 
                    : 'border-gray-200 bg-white shadow-sm hover:shadow-lg'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredId(service.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} opacity-0 transition-opacity duration-500 ${
                  isHovered ? 'opacity-5' : ''
                }`}></div>

                <div className="relative z-10">
                  {/* Service Header */}
                  <div
                    onClick={() => toggleService(service.id)}
                    className="flex items-center justify-between p-6 md:p-8 cursor-pointer group"
                  >
                    <div className="flex items-center space-x-6 flex-1">
                      {/* Number Badge */}
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg relative`}>
                        <span className="text-white font-bold text-lg">
                          {service.number}
                        </span>
                        <div className="absolute -top-1 -right-1 text-lg">
                          {service.icon}
                        </div>
                      </div>

                      {/* Title and Tag */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className={`text-2xl md:text-4xl font-bold transition-colors duration-300 ${
                            isOpen ? `text-${service.color}-600` : 'text-gray-800'
                          }`}>
                            {service.title}
                          </h3>
                          <motion.div
                            animate={{ x: isHovered ? 5 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FiArrowRight className={`text-${service.color}-500`} size={20} />
                          </motion.div>
                        </div>
                        <p className="text-gray-600 text-lg font-medium">
                          {service.tag}
                        </p>
                      </div>
                    </div>

                    {/* Toggle Button */}
                    <motion.div 
                      className={`ml-4 p-3 rounded-xl border transition-all duration-300 ${
                        isOpen 
                          ? `border-${service.color}-200 bg-${service.color}-100 text-${service.color}-600` 
                          : 'border-gray-300 bg-gray-50 text-gray-600 hover:bg-white'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isOpen ? (
                        <FiX size={24} />
                      ) : (
                        <FiPlus size={24} />
                      )}
                    </motion.div>
                  </div>

                  {/* Expandable Content */}
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-8 pb-8">
                          <div className="grid lg:grid-cols-2 gap-8 items-start">
                            {/* Description */}
                            <div className="space-y-6">
                              <p className="text-gray-700 text-lg leading-relaxed">
                                {service.description}
                              </p>
                              
                              {/* Pricing Badge */}
                              <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${service.gradient} text-white font-semibold text-sm`}>
                                {service.title === "ADVERTISEMENT" ? "Google Ads AED 599 | Meta Ads AED 599" : 
                                 service.title === "MOBILE APPLICATIONS" ? "Starting from AED 1500" :
                                 service.description.match(/AED (\d+)/)?.[0] || "Custom Pricing"}
                              </div>

                              {/* CTA Button */}
                              <button className={`group flex items-center space-x-3 px-6 py-3 rounded-xl bg-white border border-${service.color}-200 text-${service.color}-600 font-semibold hover:bg-gradient-to-r ${service.gradient} hover:text-white transition-all duration-300 shadow-sm hover:shadow-lg`}>
                                <span>Get {service.title.split(' ')[0]}</span>
                                <FiArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                              </button>
                            </div>

                            {/* Features Grid */}
                            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                              <h4 className={`text-lg font-semibold text-${service.color}-600 mb-4`}>
                                What's Included
                              </h4>
                              <div className="grid gap-3">
                                {service.items.map((item, i) => (
                                  <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.1 }}
                                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                  >
                                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.gradient}`}></div>
                                    <span className="text-gray-700 font-medium">{item}</span>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16 pt-8 border-t border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-gray-600 text-lg mb-6">
            Ready to build your next digital project?
          </p>
          <button className="group bg-gradient-to-r from-gray-900 to-gray-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <span className="flex items-center space-x-3">
              <span>Start Your Project Today</span>
              <FiArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesSection;